import find from 'lodash/find';
import get from 'lodash/get';
import map from 'lodash/map';
import reduce from 'lodash/reduce';
import { useMemo, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import communicationQueuesResource from 'src/actions/communicationQueues/communicationQueuesResource';
import { getPublishedIvrs } from 'src/presentation/components/pages/shared/helpers/ivrsHelper';
import Ivr from 'src/models/Ivrs/Ivr';
import ivrsResource from 'src/actions/ivrs/ivrsResource';
import { toastError } from 'src/actions/toast';
import useResource, {
  getConsolidatedState,
  someFailed,
  STATUS_FAILED,
  useResourceValues,
} from 'src/presentation/hooks/useResource';
import VoiceConfiguration from 'src/models/Channels/VoiceConfiguration';
import voiceConfigurationResource from 'src/actions/channels/voiceConfigurationResource';

const useVoiceConfiguration = () => {
  const dispatch = useDispatch();
  const { loadingState, values: ivrConfigurations } = useResource(ivrsResource);
  const {
    errors,
    values: voices,
    loadingState: voicesLoadingState,
  } = useResource<VoiceConfiguration>(voiceConfigurationResource);
  const { values: communicationQueues, loadingState: communicationQueuesState } =
    useResourceValues(communicationQueuesResource);

  const onlyPublishedIvrs = useMemo(() => getPublishedIvrs(ivrConfigurations), [ivrConfigurations]);

  const fetchError = someFailed(loadingState, voicesLoadingState, communicationQueuesState);
  const state = fetchError ? STATUS_FAILED : getConsolidatedState([loadingState, voicesLoadingState]);

  const endpointsCommunicationQueueName = reduce(
    voices,
    (acc, voiceConfig) => {
      const commQueue = find(communicationQueues, { endpointId: voiceConfig.id });
      if (commQueue) {
        acc[voiceConfig.id] = commQueue.name;
      }
      return acc;
    },
    {}
  );

  useEffect(() => {
    if (fetchError) {
      dispatch(toastError('Error while loading data. Please wait and try refreshing the page'));
    }
  }, [dispatch, fetchError]);

  const voiceEndpoints = useMemo(
    () =>
      map(voices, voice => {
        const initialIvr = find(onlyPublishedIvrs, (ivr: Ivr) => ivr.id === voice.initialIvrId);
        const initialIvrName = get(initialIvr, 'name', get(initialIvr, 'description', ''));

        const queueIvr = find(onlyPublishedIvrs, (ivr: Ivr) => ivr.id === voice.queueIvrId);
        const queueIvrName = get(queueIvr, 'name', get(queueIvr, 'description', ''));

        return {
          ...voice,
          name: voice.name || endpointsCommunicationQueueName[voice.id],
          initialIvrName,
          queueIvrName,
        };
      }),
    [voices, onlyPublishedIvrs, endpointsCommunicationQueueName]
  );

  return {
    errors,
    ivrConfigurations,
    loadingState: state,
    voiceEndpoints,
  };
};

export default useVoiceConfiguration;
